import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { aboutData } from '@/mocks/videos';

export default function About() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  return (
    <>
      <Navbar />
      <main className="pt-16">
        <Breadcrumb items={[{ label: '关于我们' }]} />

        {/* Hero */}
        <section className="relative w-full h-[300px] md:h-[400px] overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Modern%20creative%20office%20workspace%20with%20large%20screens%20displaying%20video%20content%2C%20warm%20natural%20light%2C%20collaborative%20team%20atmosphere%2C%20minimalist%20interior%20design%2C%20professional%20photography%20with%20clean%20composition&width=1920&height=600&seq=about-hero-qingning&orientation=landscape"
            alt="青柠影视团队"
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-black/50"></div>
          <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-4 w-full">
            <h1 className="text-3xl md:text-4xl font-bold text-white mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
              关于青柠影视
            </h1>
            <p className="text-base md:text-lg text-white/80 max-w-xl">
              {aboutData.slogan}
            </p>
          </div>
        </section>

        {/* Brand Introduction */}
        <section className="w-full bg-background-50 py-12 md:py-16">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="max-w-3xl mx-auto text-center mb-12">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-6" style={{ fontFamily: 'var(--font-heading)' }}>
                品牌介绍
              </h2>
              <p className="text-base text-foreground-600 leading-relaxed">
                {aboutData.introduction}
              </p>
            </div>

            {/* Mission & Vision */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
              <div className="p-8 bg-background-100 rounded-2xl border border-background-200">
                <div className="w-12 h-12 rounded-xl bg-primary-100 flex items-center justify-center mb-4">
                  <i className="ri-focus-2-line text-xl text-primary-500"></i>
                </div>
                <h3 className="text-lg font-semibold text-foreground-950 mb-3">我们的使命</h3>
                <p className="text-sm text-foreground-600 leading-relaxed">{aboutData.mission}</p>
              </div>
              <div className="p-8 bg-background-100 rounded-2xl border border-background-200">
                <div className="w-12 h-12 rounded-xl bg-accent-100 flex items-center justify-center mb-4">
                  <i className="ri-eye-line text-xl text-accent-500"></i>
                </div>
                <h3 className="text-lg font-semibold text-foreground-950 mb-3">我们的愿景</h3>
                <p className="text-sm text-foreground-600 leading-relaxed">{aboutData.vision}</p>
              </div>
            </div>

            {/* Values */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {aboutData.values.map((v, i) => (
                <div key={i} className="p-6 bg-background-50 rounded-xl border border-background-200 text-center">
                  <div className="w-10 h-10 rounded-full bg-secondary-100 flex items-center justify-center mx-auto mb-3">
                    <span className="text-secondary-700 font-bold text-sm">{i + 1}</span>
                  </div>
                  <h4 className="text-base font-semibold text-foreground-950 mb-2">{v.title}</h4>
                  <p className="text-xs text-foreground-500 leading-relaxed">{v.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Stats */}
        <section className="w-full bg-primary-500 py-12 md:py-16">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
              {aboutData.stats.map((stat, i) => (
                <div key={i} className="text-center">
                  <div className="text-3xl md:text-4xl font-bold text-background-50 mb-2" style={{ fontFamily: 'var(--font-heading)' }}>
                    {stat.value}
                  </div>
                  <p className="text-sm text-background-50/80">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Milestones */}
        <section className="w-full bg-background-50 py-12 md:py-16">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 text-center mb-10" style={{ fontFamily: 'var(--font-heading)' }}>
              发展历程
            </h2>
            <div className="relative">
              <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-0.5 bg-background-200 md:-translate-x-px"></div>
              <div className="flex flex-col gap-6">
                {aboutData.milestones.map((item, i) => (
                  <div key={i} className={`flex items-start gap-6 ${i % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'}`}>
                    <div className={`flex-1 ${i % 2 === 0 ? 'md:text-right' : 'md:text-left'} hidden md:block`}>
                      <div className="p-5 bg-background-100 rounded-xl border border-background-200 inline-block">
                        <p className="text-sm text-foreground-700">{item.event}</p>
                      </div>
                    </div>
                    <div className="relative z-10 flex items-center justify-center shrink-0">
                      <div className="w-8 h-8 rounded-full bg-primary-500 flex items-center justify-center">
                        <span className="text-xs text-background-50 font-bold">{i + 1}</span>
                      </div>
                    </div>
                    <div className="flex-1 md:hidden">
                      <div className="p-5 bg-background-100 rounded-xl border border-background-200">
                        <span className="text-xs font-semibold text-primary-500 mb-1 block">{item.year}</span>
                        <p className="text-sm text-foreground-700">{item.event}</p>
                      </div>
                    </div>
                    <div className="flex-1 hidden md:block">
                      {i % 2 === 1 && (
                        <div className="p-5 bg-background-100 rounded-xl border border-background-200 inline-block">
                          <span className="text-xs font-semibold text-primary-500 mb-1 block">{item.year}</span>
                          <p className="text-sm text-foreground-700">{item.event}</p>
                        </div>
                      )}
                      {i % 2 === 0 && (
                        <span className="text-xs font-semibold text-primary-500">{item.year}</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Contact */}
        <section className="w-full bg-background-100 py-12 md:py-16">
          <div className="max-w-5xl mx-auto px-4 md:px-6">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 text-center mb-10" style={{ fontFamily: 'var(--font-heading)' }}>
              联系我们
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              <div className="p-6 bg-background-50 rounded-xl border border-background-200 text-center">
                <div className="w-12 h-12 rounded-xl bg-primary-100 flex items-center justify-center mx-auto mb-3">
                  <i className="ri-mail-line text-xl text-primary-500"></i>
                </div>
                <h4 className="text-sm font-semibold text-foreground-950 mb-2">电子邮箱</h4>
                <p className="text-xs text-foreground-500">{aboutData.contact.email}</p>
              </div>
              <div className="p-6 bg-background-50 rounded-xl border border-background-200 text-center">
                <div className="w-12 h-12 rounded-xl bg-accent-100 flex items-center justify-center mx-auto mb-3">
                  <i className="ri-wechat-line text-xl text-accent-500"></i>
                </div>
                <h4 className="text-sm font-semibold text-foreground-950 mb-2">微信公众号</h4>
                <p className="text-xs text-foreground-500">{aboutData.contact.wechat}</p>
              </div>
              <div className="p-6 bg-background-50 rounded-xl border border-background-200 text-center">
                <div className="w-12 h-12 rounded-xl bg-secondary-100 flex items-center justify-center mx-auto mb-3">
                  <i className="ri-weibo-line text-xl text-secondary-500"></i>
                </div>
                <h4 className="text-sm font-semibold text-foreground-950 mb-2">新浪微博</h4>
                <p className="text-xs text-foreground-500">{aboutData.contact.weibo}</p>
              </div>
              <div className="p-6 bg-background-50 rounded-xl border border-background-200 text-center">
                <div className="w-12 h-12 rounded-xl bg-primary-100 flex items-center justify-center mx-auto mb-3">
                  <i className="ri-phone-line text-xl text-primary-500"></i>
                </div>
                <h4 className="text-sm font-semibold text-foreground-950 mb-2">客服电话</h4>
                <p className="text-xs text-foreground-500">{aboutData.contact.phone}</p>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section id="faq" className="w-full bg-background-50 py-12 md:py-16">
          <div className="max-w-3xl mx-auto px-4 md:px-6">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 text-center mb-10" style={{ fontFamily: 'var(--font-heading)' }}>
              常见问题
            </h2>
            <div className="flex flex-col gap-3">
              {aboutData.faqs.map((faq, i) => (
                <div
                  key={i}
                  className="bg-background-100 rounded-xl border border-background-200 overflow-hidden"
                >
                  <button
                    onClick={() => setOpenFaq(openFaq === i ? null : i)}
                    className="w-full flex items-center justify-between p-5 text-left cursor-pointer hover:bg-background-200/50 transition-colors"
                  >
                    <span className="text-sm font-semibold text-foreground-800 pr-4">{faq.question}</span>
                    <i
                      className={`ri-arrow-down-s-line text-lg text-foreground-400 transition-transform duration-200 shrink-0 ${
                        openFaq === i ? 'rotate-180' : ''
                      }`}
                    ></i>
                  </button>
                  <div
                    className={`transition-all duration-300 overflow-hidden ${
                      openFaq === i ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
                    }`}
                  >
                    <div className="px-5 pb-5">
                      <p className="text-sm text-foreground-600 leading-relaxed">{faq.answer}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      </main>

      <Footer />

      {/* JSON-LD for Organization & FAQPage */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Organization',
            name: '青柠影视',
            url: 'https://www.qingning.tv',
            description: aboutData.introduction,
            contactPoint: {
              '@type': 'ContactPoint',
              telephone: aboutData.contact.phone,
              email: aboutData.contact.email,
              contactType: 'customer service',
            },
            sameAs: [
              'https://weibo.com/qingningtv',
            ],
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: aboutData.faqs.map((faq) => ({
              '@type': 'Question',
              name: faq.question,
              acceptedAnswer: {
                '@type': 'Answer',
                text: faq.answer,
              },
            })),
          }),
        }}
      />
    </>
  );
}